import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface AccessControl {
  id: string
  name: string
  type: string
  roles: number
  users: number
  datasets: number
  level: string
}

interface EncryptionStatus {
  category: string
  datasets: number
  encrypted: number
  percentage: number
  algorithm: string
}

interface Statistics {
  encryption_coverage: number
  access_policies: number
  active_users: number
  security_incidents_mtd: number
  vulnerabilities_open: number
  compliance_score: number
}

export default function DataSecurityPage() {
  const [accessControls, setAccessControls] = useState<AccessControl[]>([])
  const [encryptionStatus, setEncryptionStatus] = useState<EncryptionStatus[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [accessRes, encryptionRes, statsRes] = await Promise.all([
        fetch('/api/datasecurity/access-controls'),
        fetch('/api/datasecurity/encryption'),
        fetch('/api/datasecurity/statistics')
      ])
      if (accessRes.ok) setAccessControls(await accessRes.json())
      if (encryptionRes.ok) setEncryptionStatus(await encryptionRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching data security:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Data Security</h1>
          <p className="mt-2 text-gray-600">Phase 268 - Access controls and encryption</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Encryption Coverage</p>
              <p className="text-2xl font-bold text-green-600">{statistics.encryption_coverage}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Access Policies</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.access_policies}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active Users</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.active_users}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Incidents MTD</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.security_incidents_mtd}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Open Vulnerabilities</p>
              <p className="text-2xl font-bold text-red-600">{statistics.vulnerabilities_open}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Compliance</p>
              <p className="text-2xl font-bold text-green-600">{statistics.compliance_score}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Encryption Status</h2>
          </div>
          <div className="p-6 space-y-4">
            {encryptionStatus.map((item, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{item.category}</span>
                  <span className="text-lg font-bold text-green-600">{item.percentage}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full mb-2">
                  <div className={`h-2 rounded-full ${item.percentage >= 95 ? 'bg-green-500' : item.percentage >= 80 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{width: `${item.percentage}%`}}></div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Datasets</p>
                    <p className="font-medium text-blue-600">{item.datasets}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Encrypted</p>
                    <p className="font-medium text-green-600">{item.encrypted}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Algorithm</p>
                    <p className="font-medium text-purple-600">{item.algorithm}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Access Control Policies</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Policy</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Roles</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Users</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datasets</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {accessControls.map((control) => (
                  <tr key={control.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{control.name}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{control.type}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{control.roles}</td>
                    <td className="px-6 py-4 text-blue-600">{control.users}</td>
                    <td className="px-6 py-4 text-purple-600">{control.datasets}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${control.level === 'Restricted' ? 'bg-red-100 text-red-800' : control.level === 'Confidential' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>{control.level}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
